import { skipToken, useQuery } from '@tanstack/react-query';
import type { ScoreMarket } from 'r/types';
import { fetchApiRaw, GET_MARKETS_SCORE } from 'r/utils/fetchApiRaw';

const fetchMarkets = async (filter: string, take: number) => {
  const response = await fetchApiRaw(GET_MARKETS_SCORE, {
    queryParams: { take: String(take), filter: encodeURIComponent(filter) },
  });
  return Array.isArray(response.data) ? (response.data as ScoreMarket[]) : [];
};

// Markets linked to the given analyses (Score allows one market per analysis)
export function useScoreMarketsByAnalysis(analysisIds: string[] | undefined) {
  const key = analysisIds ? [...analysisIds].sort().join(',') : '';

  return useQuery({
    queryKey: ['score-markets', 'by-analysis', key],
    queryFn:
      !analysisIds || analysisIds.length === 0 ?
        skipToken
      : () =>
          fetchMarkets(
            `"analysisId":${JSON.stringify(analysisIds)}`,
            analysisIds.length,
          ),
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useScoreMarketByAnalysis(analysisId: string | undefined) {
  const { data, ...rest } = useScoreMarketsByAnalysis(
    analysisId ? [analysisId] : undefined,
  );
  return { data: data?.[0], ...rest };
}

export function useScoreMarket(marketId: string | undefined) {
  return useQuery({
    queryKey: ['score-markets', 'one', marketId],
    queryFn:
      !marketId ? skipToken : (
        async () =>
          (await fetchMarkets(`"id":${JSON.stringify(marketId)}`, 1))[0] ?? null
      ),
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
